import { Add, Delete, PlaylistAdd, Undo, Save } from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Stack,
  TextField,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import type {
  ILegalDependencyHeaderProps,
  IRuleSelectionOption,
} from '../../../Interfaces/ILegalDependency';

export const LegalDependencyHeader = ({
  canSaveRule,
  isActionToolbarBusy,
  isDeletePending,
  isDependenciesLoading,
  isSavePending,
  onAddAction,
  onAddConditionGroup,
  onDeleteRule,
  onResetBuilder,
  onResetToCurrentRule,
  onRuleNameChange,
  onRuleSelectionChange,
  onSaveRule,
  ruleName,
  ruleSelectionOptions,
  selectedRuleId,
}: ILegalDependencyHeaderProps) => (
  <Card
    variant='outlined'
    sx={{
      background: theme =>
        `linear-gradient(180deg, ${alpha(
          theme.palette.info.light,
          0.12
        )} 0%, ${theme.palette.background.paper} 100%)`,
      borderRadius: 3,
    }}
  >
    <CardContent sx={{ p: { xs: 2.5, md: 3.5 } }}>
      <Stack
        alignItems={{ xs: 'flex-start', md: 'center' }}
        direction={{ xs: 'column', md: 'row' }}
        justifyContent='space-between'
        spacing={2}
      >
        <Box sx={{ maxWidth: 760 }}>
          <Typography variant='h5'>Abhängigkeitsregel</Typography>
          <Typography color='text.secondary' sx={{ mt: 0.75 }} variant='body2'>
            Werden die auf der linken Seite festgelegten Bedingungen erfüllt,
            werden die auf der rechten Seite ausgewählten Phasen oder Maßnahmen
            automatisch umgesetzt.
          </Typography>
        </Box>

        <Stack spacing={1.25} sx={{ width: { xs: '100%', xl: 'auto' } }}>
          {/* Rule selector + name */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} useFlexGap>
            <Autocomplete<IRuleSelectionOption, false, false, false>
              getOptionLabel={option => option.label}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              loading={isDependenciesLoading}
              onChange={(_, value) => onRuleSelectionChange(value?.id ?? null)}
              options={ruleSelectionOptions}
              renderInput={params => (
                <TextField
                  {...params}
                  label='Gespeicherte Regeln'
                  placeholder='Regel auswählen'
                  size='small'
                />
              )}
              size='small'
              sx={{ minWidth: { xs: '100%', sm: 220 } }}
              value={
                selectedRuleId === null
                  ? null
                  : ruleSelectionOptions.find(
                      option => option.id === selectedRuleId
                    ) || null
              }
            />
            <TextField
              label='Regelname'
              onChange={event => onRuleNameChange(event.target.value)}
              size='small'
              sx={{ minWidth: { xs: '100%', sm: 200 } }}
              value={ruleName}
            />
          </Stack>

          {/* Action toolbar */}
          <Stack
            direction='row'
            flexWrap='wrap'
            spacing={1}
            useFlexGap
            sx={{ alignItems: 'center' }}
          >
            {/* Regel actions */}
            <Tooltip title='Neue leere Regel erstellen'>
              <span>
                <Button
                  disabled={isActionToolbarBusy}
                  onClick={onResetBuilder}
                  size='small'
                  startIcon={<PlaylistAdd />}
                  variant='outlined'
                >
                  Neue Regel
                </Button>
              </span>
            </Tooltip>
            <Tooltip
              title={
                selectedRuleId !== null
                  ? 'Änderungen verwerfen und Regel neu laden'
                  : 'Keine Regel geladen'
              }
            >
              <span>
                <Button
                  disabled={selectedRuleId === null || isActionToolbarBusy}
                  onClick={onResetToCurrentRule}
                  size='small'
                  startIcon={<Undo fontSize='small' />}
                  variant='outlined'
                >
                  Verwerfen
                </Button>
              </span>
            </Tooltip>
            <Tooltip
              title={
                !canSaveRule
                  ? 'Bedingung und Aktion müssen vollständig sein'
                  : 'Regel speichern'
              }
            >
              <span>
                <Button
                  disabled={!canSaveRule || isActionToolbarBusy}
                  onClick={onSaveRule}
                  size='small'
                  startIcon={
                    isSavePending ? (
                      <CircularProgress color='inherit' size={16} />
                    ) : (
                      <Save fontSize='small' />
                    )
                  }
                  variant='contained'
                >
                  Speichern
                </Button>
              </span>
            </Tooltip>
            <Tooltip
              title={
                selectedRuleId === null
                  ? 'Keine Regel ausgewählt'
                  : 'Regel löschen'
              }
            >
              <span>
                <Button
                  color='error'
                  disabled={selectedRuleId === null || isActionToolbarBusy}
                  onClick={onDeleteRule}
                  size='small'
                  startIcon={
                    isDeletePending ? (
                      <CircularProgress color='inherit' size={16} />
                    ) : (
                      <Delete fontSize='small' />
                    )
                  }
                  variant='outlined'
                >
                  Löschen
                </Button>
              </span>
            </Tooltip>

            <Divider flexItem orientation='vertical' sx={{ mx: 0.5 }} />

            {/* Builder actions */}
            <Button
              disabled={isActionToolbarBusy}
              onClick={onAddConditionGroup}
              size='small'
              startIcon={<Add />}
              variant='text'
            >
              Bedingungsgruppe
            </Button>
            <Button
              disabled={isActionToolbarBusy}
              onClick={onAddAction}
              size='small'
              startIcon={<Add />}
              variant='text'
            >
              Aktion
            </Button>
          </Stack>

          {isActionToolbarBusy ? (
            <Stack
              alignItems='center'
              direction='row'
              spacing={1}
              sx={{ minHeight: 24 }}
            >
              <CircularProgress size={16} />
              <Typography color='text.secondary' variant='caption'>
                {isDeletePending
                  ? 'Regel wird gelöscht...'
                  : isSavePending
                    ? 'Regel wird gespeichert...'
                    : 'Daten werden geladen...'}
              </Typography>
            </Stack>
          ) : null}
        </Stack>
      </Stack>
    </CardContent>
  </Card>
);
